import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Clock, Calendar, Shield, Bell, CreditCard, Store, Plus, Trash2 } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import {
  settingsApi,
  type SettingsGroup,
  type BusinessHours,
  type Holiday,
  type CreateHolidayRequest,
  type UpdateBusinessHoursRequest,
} from '../../api/settingsApi';
import { useToast } from '../../components/ui/Toast/ToastContext';

// Tab configuration
const TABS = [
  { id: 'general', label: 'General', icon: Store },
  { id: 'booking', label: 'Booking', icon: Calendar },
  { id: 'payment', label: 'Payment', icon: CreditCard },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'hours', label: 'Business Hours', icon: Clock },
  { id: 'holidays', label: 'Holidays', icon: Calendar },
] as const;

type TabId = (typeof TABS)[number]['id'];

export const SettingsPage: FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Fetch settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: settingsApi.getAll,
  });

  // Fetch business hours
  const { data: businessHours, isLoading: hoursLoading } = useQuery({
    queryKey: ['admin-business-hours'],
    queryFn: settingsApi.getBusinessHours,
  });

  // Fetch holidays
  const { data: holidays, isLoading: holidaysLoading } = useQuery({
    queryKey: ['admin-holidays'],
    queryFn: settingsApi.getHolidays,
  });

  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: ({ category, key, value }: { category: string; key: string; value: unknown }) =>
      settingsApi.updateSetting(category, key, value),
    onSuccess: () => {
      showToast({ type: 'success', message: 'Setting updated successfully' });
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
    onError: (error: Error) => {
      showToast({ type: 'error', message: error.message || 'Failed to update setting' });
    },
  });

  // Update business hours mutation
  const updateHoursMutation = useMutation({
    mutationFn: ({ day, data }: { day: number; data: UpdateBusinessHoursRequest }) =>
      settingsApi.updateBusinessHours(day, data),
    onSuccess: () => {
      showToast({ type: 'success', message: 'Business hours updated' });
      queryClient.invalidateQueries({ queryKey: ['admin-business-hours'] });
    },
    onError: (error: Error) => {
      showToast({ type: 'error', message: error.message || 'Failed to update hours' });
    },
  });

  // Create holiday mutation
  const createHolidayMutation = useMutation({
    mutationFn: settingsApi.createHoliday,
    onSuccess: () => {
      showToast({ type: 'success', message: 'Holiday created' });
      queryClient.invalidateQueries({ queryKey: ['admin-holidays'] });
    },
    onError: (error: Error) => {
      showToast({ type: 'error', message: error.message || 'Failed to create holiday' });
    },
  });

  // Delete holiday mutation
  const deleteHolidayMutation = useMutation({
    mutationFn: settingsApi.deleteHoliday,
    onSuccess: () => {
      showToast({ type: 'success', message: 'Holiday deleted' });
      queryClient.invalidateQueries({ queryKey: ['admin-holidays'] });
    },
    onError: (error: Error) => {
      showToast({ type: 'error', message: error.message || 'Failed to delete holiday' });
    },
  });

  const isLoading = settingsLoading || hoursLoading || holidaysLoading;

  // Get settings for current category tab
  const getCategorySettings = (category: string): SettingsGroup | undefined => {
    return settings?.find((g) => g.category.toLowerCase() === category);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">System Settings</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            Configure system-wide settings and business preferences
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-slate-700">
          <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Settings tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium
                    ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-600'
                    }
                  `}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
            {/* Settings Tabs (general, booking, payment, notifications, security) */}
            {['general', 'booking', 'payment', 'notifications', 'security'].includes(activeTab) && (
              <SettingsForm
                category={activeTab.toUpperCase()}
                settings={getCategorySettings(activeTab)}
                onUpdate={(key, value) =>
                  updateSettingMutation.mutate({ category: activeTab.toUpperCase(), key, value })
                }
                isUpdating={updateSettingMutation.isPending}
              />
            )}

            {/* Business Hours Tab */}
            {activeTab === 'hours' && businessHours && (
              <BusinessHoursEditor
                hours={businessHours}
                onUpdate={(day, data) => updateHoursMutation.mutate({ day, data })}
                isUpdating={updateHoursMutation.isPending}
              />
            )}

            {/* Holidays Tab */}
            {activeTab === 'holidays' && holidays && (
              <HolidaysEditor
                holidays={holidays}
                onCreate={(data) => createHolidayMutation.mutate(data)}
                onDelete={(id) => deleteHolidayMutation.mutate(id)}
                isCreating={createHolidayMutation.isPending}
                isDeleting={deleteHolidayMutation.isPending}
              />
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Settings Form Component
interface SettingsFormProps {
  category: string;
  settings?: SettingsGroup;
  onUpdate: (key: string, value: unknown) => void;
  isUpdating: boolean;
}

const SettingsForm: FC<SettingsFormProps> = ({ category, settings, onUpdate, isUpdating }) => {
  if (!settings) {
    return (
      <div className="text-center py-8">
        <Settings className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-slate-400">No settings found for this category.</p>
      </div>
    );
  }

  // Format category name for display
  const formatCategory = (cat: string) => {
    return cat.charAt(0) + cat.slice(1).toLowerCase();
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
        {formatCategory(category)} Settings
      </h3>
      <div className="grid gap-6">
        {settings.settings.map((setting) => (
          <SettingField
            key={setting.key}
            setting={setting}
            onUpdate={(value) => onUpdate(setting.key, value)}
            isUpdating={isUpdating}
          />
        ))}
      </div>
    </div>
  );
};

// Individual Setting Field
interface SettingFieldProps {
  setting: {
    key: string;
    value: unknown;
    dataType: string;
    description: string;
  };
  onUpdate: (value: unknown) => void;
  isUpdating: boolean;
}

const SettingField: FC<SettingFieldProps> = ({ setting, onUpdate, isUpdating }) => {
  const [localValue, setLocalValue] = useState(setting.value);
  const hasChanged = JSON.stringify(localValue) !== JSON.stringify(setting.value);

  const handleSave = () => {
    onUpdate(localValue);
  };

  // Format key for display (camelCase to Title Case)
  const formatKey = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-4 pb-4 border-b border-gray-100 dark:border-slate-700 last:border-0">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {formatKey(setting.key)}
        </label>
        <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">{setting.description}</p>
      </div>
      <div className="flex items-center gap-2 sm:w-72">
        {setting.dataType === 'BOOLEAN' ? (
          <button
            type="button"
            onClick={() => {
              const newValue = !localValue;
              setLocalValue(newValue);
              onUpdate(newValue);
            }}
            disabled={isUpdating}
            className={`
              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${localValue ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-600'}
              ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            role="switch"
            aria-checked={localValue as boolean}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${localValue ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        ) : setting.dataType === 'NUMBER' ? (
          <>
            <input
              type="number"
              value={localValue as number}
              onChange={(e) => setLocalValue(Number(e.target.value))}
              className="block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {hasChanged && (
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
            )}
          </>
        ) : setting.dataType === 'STRING' ? (
          <>
            <input
              type="text"
              value={localValue as string}
              onChange={(e) => setLocalValue(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {hasChanged && (
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
            )}
          </>
        ) : (
          <pre className="text-xs bg-gray-50 dark:bg-slate-700 dark:text-slate-300 p-2 rounded overflow-auto max-w-full">
            {JSON.stringify(localValue, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

// Business Hours Editor Component
interface BusinessHoursEditorProps {
  hours: BusinessHours[];
  onUpdate: (day: number, data: UpdateBusinessHoursRequest) => void;
  isUpdating: boolean;
}

const BusinessHoursEditor: FC<BusinessHoursEditorProps> = ({ hours, onUpdate, isUpdating }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">Business Hours</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Configure your operating hours for each day of the week
        </p>
      </div>
      <div className="space-y-4">
        {hours.map((day) => (
          <div
            key={day.dayOfWeek}
            className="flex flex-col sm:flex-row sm:items-center gap-4 pb-4 border-b border-gray-100 dark:border-slate-700 last:border-0"
          >
            <div className="w-28 font-medium text-gray-700 dark:text-slate-300">{day.dayName}</div>
            <button
              onClick={() =>
                onUpdate(day.dayOfWeek, {
                  isOpen: !day.isOpen,
                  openTime: day.openTime,
                  closeTime: day.closeTime,
                })
              }
              disabled={isUpdating}
              className={`
                px-3 py-1 text-sm rounded-full font-medium transition-colors
                ${
                  day.isOpen
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                }
                ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}
              `}
            >
              {day.isOpen ? 'Open' : 'Closed'}
            </button>
            {day.isOpen && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                <input
                  type="time"
                  value={day.openTime}
                  onChange={(e) =>
                    onUpdate(day.dayOfWeek, {
                      isOpen: day.isOpen,
                      openTime: e.target.value,
                      closeTime: day.closeTime,
                    })
                  }
                  className="rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  disabled={isUpdating}
                />
                <span>to</span>
                <input
                  type="time"
                  value={day.closeTime}
                  onChange={(e) =>
                    onUpdate(day.dayOfWeek, {
                      isOpen: day.isOpen,
                      openTime: day.openTime,
                      closeTime: e.target.value,
                    })
                  }
                  className="rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  disabled={isUpdating}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Holidays Editor Component
interface HolidaysEditorProps {
  holidays: Holiday[];
  onCreate: (data: CreateHolidayRequest) => void;
  onDelete: (id: string) => void;
  isCreating: boolean;
  isDeleting: boolean;
}

const HolidaysEditor: FC<HolidaysEditorProps> = ({
  holidays,
  onCreate,
  onDelete,
  isCreating,
  isDeleting,
}) => {
  const [newHoliday, setNewHoliday] = useState<CreateHolidayRequest>({
    name: '',
    date: '',
    isRecurring: false,
  });

  const handleCreate = () => {
    if (newHoliday.name && newHoliday.date) {
      onCreate(newHoliday);
      setNewHoliday({ name: '', date: '', isRecurring: false });
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
          Holidays & Closures
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Add holidays or special closure dates
        </p>
      </div>

      {/* Add new holiday form */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
        <input
          type="text"
          placeholder="Holiday name"
          value={newHoliday.name}
          onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
          className="flex-1 rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:placeholder-slate-400"
        />
        <input
          type="date"
          value={newHoliday.date}
          onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
          className="rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={newHoliday.isRecurring}
            onChange={(e) => setNewHoliday({ ...newHoliday, isRecurring: e.target.checked })}
            className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
          />
          Annual
        </label>
        <button
          onClick={handleCreate}
          disabled={isCreating || !newHoliday.name || !newHoliday.date}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          Add Holiday
        </button>
      </div>

      {/* Holidays list */}
      <div className="space-y-2">
        {holidays.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-slate-400">No holidays configured.</p>
          </div>
        ) : (
          holidays.map((holiday) => (
            <div
              key={holiday.id}
              className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div>
                  <span className="font-medium text-gray-900 dark:text-slate-100">
                    {holiday.name}
                  </span>
                  <span className="ml-3 text-sm text-gray-500 dark:text-slate-400">
                    {formatDate(holiday.date)}
                  </span>
                </div>
                {holiday.isRecurring && (
                  <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full">
                    Annual
                  </span>
                )}
                {holiday.isClosed && (
                  <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded-full">
                    Closed
                  </span>
                )}
              </div>
              <button
                onClick={() => onDelete(holiday.id)}
                disabled={isDeleting}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 transition-colors"
                aria-label={`Remove ${holiday.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
